import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaFilter,
  FaEdit,
  FaEye,
  FaFileUpload,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaExclamationTriangle,
  FaChartLine,
  FaMoneyBillWave,
  FaBuilding,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaDownload,
  FaTrash,
  FaPlus,
} from 'react-icons/fa';

const CorporateCreditVerify = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('all');
  const [riskLevel, setRiskLevel] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editForm, setEditForm] = useState({
    creditLimit: '',
    riskLevel: '',
    verificationStatus: '',
    documents: [],
  });

  const itemsPerPage = 7;

  // Enhanced mock data with more realistic fields
  const mockCreditProfiles = [
    {
      id: 'CP-001',
      companyName: 'Tech Solutions Inc.',
      companyId: 'CORP-001',
      creditLimit: 5000000,
      outstanding: 1250000,
      utilized: 25,
      riskLevel: 'low',
      verificationStatus: 'verified',
      lastVerified: '2024-01-15',
      creditScore: 85,
      contactPerson: 'John Smith',
      email: 'john@techsolutions.com',
      phone: '+91-9876543210',
      address: 'Bangalore, Karnataka',
      documents: ['company_registration.pdf', 'bank_statement.pdf'],
    },
    {
      id: 'CP-002',
      companyName: 'Global Innovations Ltd.',
      companyId: 'CORP-002',
      creditLimit: 2500000,
      outstanding: 2450000,
      utilized: 98,
      riskLevel: 'high',
      verificationStatus: 'pending',
      lastVerified: '2024-02-01',
      creditScore: 45,
      contactPerson: 'Sarah Johnson',
      email: 'sarah@globalinnovations.com',
      phone: '+91-9876543211',
      address: 'Mumbai, Maharashtra',
      documents: ['company_registration.pdf'],
    },
    {
      id: 'CP-003',
      companyName: 'Enterprise Systems Co.',
      companyId: 'CORP-003',
      creditLimit: 10000000,
      outstanding: 3500000,
      utilized: 35,
      riskLevel: 'medium',
      verificationStatus: 'verified',
      lastVerified: '2024-01-20',
      creditScore: 72,
      contactPerson: 'Mike Chen',
      email: 'mike@enterprisesys.com',
      phone: '+91-9876543212',
      address: 'Hyderabad, Telangana',
      documents: ['company_registration.pdf', 'bank_statement.pdf', 'gst_certificate.pdf'],
    },
    {
      id: 'CP-004',
      companyName: 'Digital Transform LLC',
      companyId: 'CORP-004',
      creditLimit: 3000000,
      outstanding: 0,
      utilized: 0,
      riskLevel: 'low',
      verificationStatus: 'rejected',
      lastVerified: '2024-03-01',
      creditScore: 68,
      contactPerson: 'Emily Davis',
      email: 'emily@digitaltransform.com',
      phone: '+91-9876543213',
      address: 'Chennai, Tamil Nadu',
      documents: ['company_registration.pdf'],
    },
    {
      id: 'CP-005',
      companyName: 'Cloud Services Corp.',
      companyId: 'CORP-005',
      creditLimit: 7500000,
      outstanding: 1800000,
      utilized: 24,
      riskLevel: 'low',
      verificationStatus: 'verified',
      lastVerified: '2024-02-15',
      creditScore: 88,
      contactPerson: 'Robert Brown',
      email: 'robert@cloudservices.com',
      phone: '+91-9876543214',
      address: 'Pune, Maharashtra',
      documents: ['company_registration.pdf', 'bank_statement.pdf'],
    },
    {
      id: 'CP-006',
      companyName: 'Data Analytics Ltd.',
      companyId: 'CORP-006',
      creditLimit: 4000000,
      outstanding: 3950000,
      utilized: 99,
      riskLevel: 'high',
      verificationStatus: 'pending',
      lastVerified: '2024-03-10',
      creditScore: 38,
      contactPerson: 'Lisa Wang',
      email: 'lisa@dataanalytics.com',
      phone: '+91-9876543215',
      address: 'Delhi, NCR',
      documents: ['company_registration.pdf', 'bank_statement.pdf'],
    },
  ];

  // Filter profiles based on search and filters
  const filteredProfiles = mockCreditProfiles.filter((profile) => {
    const matchesSearch =
      profile.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      profile.companyId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVerification =
      verificationStatus === 'all' || profile.verificationStatus === verificationStatus;
    const matchesRisk = riskLevel === 'all' || profile.riskLevel === riskLevel;

    return matchesSearch && matchesVerification && matchesRisk;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProfiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProfiles = filteredProfiles.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleEdit = (profile) => {
    setSelectedCompany(profile);
    setEditForm({
      creditLimit: profile.creditLimit.toString(),
      riskLevel: profile.riskLevel,
      verificationStatus: profile.verificationStatus,
      documents: profile.documents || [],
    });
    setIsModalOpen(true);
  };

  const handleView = (profile) => {
    setSelectedCompany(profile);
    // You can open a view-only modal here if needed
    console.log('Viewing profile:', profile);
  };

  const handleSave = () => {
    // In a real app, this would make an API call
    console.log('Saving profile:', selectedCompany?.id, editForm);
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setEditForm((prev) => ({
      ...prev,
      documents: [...prev.documents, ...files.map((f) => f.name)],
    }));
  };

  const removeDocument = (index) => {
    setEditForm((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getRiskBadge = (level) => {
    const riskConfig = {
      low: { color: 'bg-green-100 text-green-800 border-green-200', icon: FaCheckCircle },
      medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: FaClock },
      high: { color: 'bg-red-100 text-red-800 border-red-200', icon: FaExclamationTriangle },
    };

    const config = riskConfig[level] || riskConfig.medium;
    const IconComponent = config.icon;

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${config.color}`}
      >
        <IconComponent className="text-xs" />
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      verified: { color: 'bg-green-100 text-green-800 border-green-200', icon: FaCheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: FaClock },
      rejected: { color: 'bg-red-100 text-red-800 border-red-200', icon: FaTimesCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${config.color}`}
      >
        <IconComponent className="text-xs" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getCreditScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return 'text-red-600';
    if (utilization >= 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Calculate stats
  const totalCreditLimit = mockCreditProfiles.reduce(
    (sum, profile) => sum + profile.creditLimit,
    0
  );
  const totalOutstanding = mockCreditProfiles.reduce(
    (sum, profile) => sum + profile.outstanding,
    0
  );
  const pendingVerification = mockCreditProfiles.filter(
    (profile) => profile.verificationStatus === 'pending'
  ).length;
  const highRiskAccounts = mockCreditProfiles.filter(
    (profile) => profile.riskLevel === 'high'
  ).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header Section */}
      <header className="flex justify-between items-center mb-3 bg-white p-3">
        <h1 className="text-black text-xl font-bold ml-2">Customer Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="btn "
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="btn"
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="btn"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="btn"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="btn text-black font-bold"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="btn "
          >
            Settings
          </button>
        </div>
      </header>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 font-medium">Total Credit Limit</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(totalCreditLimit)}
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <FaMoneyBillWave className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 font-medium">Total Outstanding</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCurrency(totalOutstanding)}
              </div>
            </div>
            <div className="p-3 bg-orange-50 rounded-lg">
              <FaChartLine className="text-orange-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 font-medium">Pending Verification</div>
              <div className="text-2xl font-bold text-yellow-600 mt-1">
                {pendingVerification} Companies
              </div>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <FaClock className="text-yellow-600 text-xl" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 font-medium">High Risk Accounts</div>
              <div className="text-2xl font-bold text-red-600 mt-1">
                {highRiskAccounts} Companies
              </div>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <FaExclamationTriangle className="text-red-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="bg-white rounded-lg border border-gray-200">
        {/* Filter Bar */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Company Name or ID"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <select
                  value={verificationStatus}
                  onChange={(e) => setVerificationStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>

                <select
                  value={riskLevel}
                  onChange={(e) => setRiskLevel(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Risk Levels</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* Add Credit Profile Button */}
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center space-x-2"
            >
              <FaPlus className="text-sm" />
              <span>Add Credit Profile</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Company
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Credit Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Credit Score
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Risk Level
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentProfiles.map((profile) => (
                <tr key={profile.id} className="hover:bg-gray-50 transition duration-150">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {profile.companyName}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{profile.companyId}</div>
                      <div className="flex items-center mt-1 text-xs text-gray-600">
                        <FaUser className="mr-1 text-xs" />
                        {profile.contactPerson}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(profile.creditLimit)}
                      </div>
                      <div className="text-xs text-gray-600">
                        Outstanding: {formatCurrency(profile.outstanding)}
                      </div>
                      <div
                        className={`text-xs font-medium ${getUtilizationColor(profile.utilized)}`}
                      >
                        Utilized: {profile.utilized}%
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-lg font-bold ${getCreditScoreColor(profile.creditScore)}`}
                    >
                      {profile.creditScore}
                    </span>
                  </td>
                  <td className="px-3 py-4">{getRiskBadge(profile.riskLevel)}</td>
                  <td className="px-3 py-4">{getStatusBadge(profile.verificationStatus)}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleEdit(profile)}
                        className="text-gray-600 hover:text-blue-600 transition duration-150"
                        title="Edit credit profile"
                      >
                        <FaEdit size={16} />
                      </button>
                      <button
                        onClick={() => handleView(profile)}
                        className="text-gray-600 hover:text-green-600 transition duration-150"
                        title="View details"
                      >
                        <FaEye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {currentProfiles.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-4xl mb-4">
                <FaChartLine />
              </div>
              <p className="text-gray-500 text-lg mb-4">
                No credit profiles found matching your criteria.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center space-x-2 mx-auto"
              >
                <FaPlus className="text-sm" />
                <span>Add Credit Profile</span>
              </button>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to{' '}
                {Math.min(startIndex + itemsPerPage, filteredProfiles.length)} of{' '}
                {filteredProfiles.length} profiles
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg font-semibold transition duration-200 ${
                      currentPage === page
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition duration-200 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FaChartLine className="text-2xl text-gray-800" />
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {selectedCompany ? 'Edit Credit Profile' : 'Add Credit Profile'}
                    </h2>
                    {selectedCompany && (
                      <p className="text-gray-600 mt-1">{selectedCompany.companyName}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Company Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={selectedCompany?.companyName || ''}
                    readOnly={!!selectedCompany}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company ID</label>
                  <input
                    type="text"
                    value={selectedCompany?.companyId || ''}
                    readOnly={!!selectedCompany}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                    placeholder="Enter company ID"
                  />
                </div>
              </div>

              {/* Credit Information */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Limit (₹)
                  </label>
                  <input
                    type="number"
                    value={editForm.creditLimit}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, creditLimit: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter credit limit"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Risk Level</label>
                  <select
                    value={editForm.riskLevel}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, riskLevel: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Risk Level</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Status
                  </label>
                  <select
                    value={editForm.verificationStatus}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, verificationStatus: e.target.value }))
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Document Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Verification Documents
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <FaFileUpload className="mx-auto text-3xl text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 mb-3">
                      Upload company registration, bank statements, and other verification documents
                    </p>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="document-upload"
                    />
                    <label
                      htmlFor="document-upload"
                      className="cursor-pointer bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition duration-200 font-semibold inline-flex items-center space-x-2"
                    >
                      <FaFileUpload className="text-sm" />
                      <span>Choose Files</span>
                    </label>
                  </div>

                  {/* Uploaded files list */}
                  {editForm.documents.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Uploaded Documents:
                      </h4>
                      {editForm.documents.map((doc, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded"
                        >
                          <div className="flex items-center space-x-2">
                            <FaFileUpload className="text-gray-400" />
                            <span className="text-sm text-gray-700 truncate">{doc}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-semibold"
              >
                {selectedCompany ? 'Update Profile' : 'Create Profile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorporateCreditVerify;
